import React from 'react';
import { FormControl, Select, MenuItem } from '@mui/material';
import { withTranslation } from 'react-i18next';
import PACESlider from './paceSlider';

const nullifyUndefined = (value) => (value || null)
const reKeyArray = (arr) => arr.filter(_ => true)

// Helper function to format date from mm/dd/yyyy to mmm dd, yyyy
const formatDateString = (dateString) => {
  if (!dateString) return '';

  const dateParts = dateString.split('/');
  if (dateParts.length >= 3) {
    const month = parseInt(dateParts[0]);
    const day = parseInt(dateParts[1]);
    const year = parseInt(dateParts[2]);

    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(wbConfig.userLocale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  return dateString;
}

// Helper function to calculate font size based on number of categories
const getFontSizeForCategories = (categoriesCount) => {
  if (categoriesCount <= 7) return '12px';
  if (categoriesCount <= 14) return '10px';
  if (categoriesCount <= 21) return '9px';
  if (categoriesCount <= 30) return '8px';
  return '7px'; // For very long ranges
}

// Performance, Alignment, Competence and Engagement
class MeetingPaceSection extends React.Component {
  // Convert backend range format to frontend display format
  static convertBackendRangeToDisplay(paceDataRange) {
    // If backend provides a range type directly
    if (paceDataRange.rangeType) {
      return paceDataRange.rangeType; // e.g., "Last 3 months"
    }

    // If backend provides start/end dates, calculate the range
    if (paceDataRange.startDate && paceDataRange.endDate) {
      const start = new Date(paceDataRange.startDate);
      const end = new Date(paceDataRange.endDate);

      // Calculate approximate time difference
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Map to display options
      if (diffDays <= 35) return 'Last month';
      if (diffDays <= 95) return 'Last 3 months';
      if (diffDays <= 185) return 'Last 6 months';
      if (diffDays <= 370) return 'Last year';
      if (diffDays <= 735) return 'Last 2 years';

      return 'Last 3 months'; // Default fallback
    }
    return null;
  }

  // Helper function to convert time range strings to unix timestamps
  static getDateRangeFromSelection(timeRange, meetingDate = null) {
    // Use meeting date if provided, otherwise use current date
    const baseDate = meetingDate ? new Date(meetingDate * 1000) : new Date();
    const endDate = new Date(baseDate);
    const startDate = new Date(baseDate);

    switch (timeRange) {
      case 'Last month':
        startDate.setMonth(startDate.getMonth() - 1);
        break;
      case 'Last 3 months':
        startDate.setMonth(startDate.getMonth() - 3);
        break;
      case 'Last 6 months':
        startDate.setMonth(startDate.getMonth() - 6);
        break;
      case 'Last year':
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
      case 'Last 2 years':
        startDate.setFullYear(startDate.getFullYear() - 2);
        break;
      default:
        // Default to last 3 months
        startDate.setMonth(startDate.getMonth() - 3);
        break;
    }

    // Return unix timestamps (seconds since epoch)
    return {
      startTime: Math.floor(startDate.getTime() / 1000),
      endTime: Math.floor(endDate.getTime() / 1000)
    };
  }

  // Format JS Date to 'n/j/Y' (e.g., 8/7/2025)
  static formatDateForXAxis(date) {
    const m = date.getMonth() + 1;
    const d = date.getDate();
    const y = date.getFullYear();
    return `${m}/${d}/${y}`;
  }

  static parseXAxisDate(str) {
    const [m, d, y] = str.split('/').map(Number);
    return new Date(y, m - 1, d);
  }

  static clampDate(year, monthIndex, day) {
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    return new Date(year, monthIndex, Math.min(day, lastDay));
  }

  // Generate monthly anchors from startTime..endTime anchored to endDate's day-of-month
  static generateMonthlyAnchors(startTime, endTime) {
    if (!startTime || !endTime) return [];
    const start = new Date(startTime * 1000);
    const end = new Date(endTime * 1000);

    // Normalize to midnight to avoid excluding the start month due to time-of-day
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    const dayAnchor = endDay.getDate();

    const anchors = [];
    anchors.push(MeetingPaceSection.formatDateForXAxis(endDay)); // always include end date

    let y = endDay.getFullYear();
    let m = endDay.getMonth();
    while (true) {
      m -= 1;
      if (m < 0) { m = 11; y -= 1; }
      const dt = MeetingPaceSection.clampDate(y, m, dayAnchor); // midnight
      if (dt.getTime() < startDay.getTime()) break; // include when >= start
      anchors.push(MeetingPaceSection.formatDateForXAxis(dt));
    }

    anchors.reverse();
    return anchors;
  }

  // Extend backend X with whole time range and align meetingOrder by date
  static extendXAxisWithTimeRange(backendX, backendMeetingOrder, startTime, endTime) {
    // Generate monthly anchors and remove any dates already present in backend X
    const monthlyAll = MeetingPaceSection.generateMonthlyAnchors(startTime, endTime);
    const backendSet = new Set(backendX); // keep backend duplicates as-is
    const monthly = monthlyAll.filter(d => !backendSet.has(d)); // avoid frontend-added dupes

    // Build date -> queue of meetingIds preserving order, including duplicates
    const dateToIds = {};
    backendX.forEach((d, i) => {
      (dateToIds[d] = dateToIds[d] || []).push(backendMeetingOrder[i] || null);
    });

    // Combine monthly anchors + backendX, stable sort by date
    const combined = [
      ...monthly.map((d, idx) => ({ d, ts: MeetingPaceSection.parseXAxisDate(d).getTime(), type: 'm', idx })),
      ...backendX.map((d, idx) => ({ d, ts: MeetingPaceSection.parseXAxisDate(d).getTime(), type: 'b', idx })),
    ].sort((a, b) => {
      if (a.ts !== b.ts) return a.ts - b.ts;
      if (a.type !== b.type) return a.type === 'm' ? -1 : 1; // month anchors before backend on same day
      return a.idx - b.idx; // stable within each group
    });

    // Rebuild categories and aligned meetingOrder (keep plots relative to original)
    const categories = [];
    const alignedMeetingOrder = [];
    combined.forEach(item => {
      categories.push(item.d);
      if (dateToIds[item.d] && dateToIds[item.d].length) {
        alignedMeetingOrder.push(dateToIds[item.d].shift());
      } else {
        alignedMeetingOrder.push(null);
      }
    });

    return { categories, meetingOrder: alignedMeetingOrder };
  }

  constructor (props) {
    super(props);
    let parsedPrefs = {
      epa_graph: { [this.props.currentUser.id]: 0 },
    };
    if (this.props.meeting.preferences && typeof this.props.meeting.preferences === 'string') {
      parsedPrefs = JSON.parse(this.props.meeting.preferences)
    }
    if (this.props.meeting.preferences
        && typeof this.props.meeting.preferences === 'object'
        && Object.keys(this.props.meeting.preferences).length) {
      parsedPrefs = this.props.meeting.preferences
    }
    this.state = {
      pace_values: this.defaultPaceValue(),
      coaching_text: this.coachingText(),
      preferences: {
        epa_graph: (parsedPrefs.epa_graph && parsedPrefs.epa_graph[this.props.currentUser.id]) ? 1 : 0,
      },
      timeRange: 'Last 3 months',
      highlightedAttribute: null,
      activeSlider: null,
      sliderTooltip: {
        visible: false,
        attributeType: null,
        position: { x: 0, y: 0 },
        data: null
      }
    };

    this.defaultPaceValue = this.defaultPaceValue.bind(this);
    this.coachingText = this.coachingText.bind(this);
    this.defaultPaceColors = this.defaultPaceColors.bind(this);
    this.getEpaColor = this.getEpaColor.bind(this);
    this.updateCoachingText = this.updateCoachingText.bind(this);
    this.arrowClick = this.arrowClick.bind(this);
    this.drawEPA = this.drawEPA.bind(this);
    this.buildSeriesItem = this.buildSeriesItem.bind(this);
    this.buildSeriesConfig = this.buildSeriesConfig.bind(this);
    this.renderEPAGraph = this.renderEPAGraph.bind(this);
    this.updateGraph = this.updateGraph.bind(this);
    this.handleTimeRangeChange = this.handleTimeRangeChange.bind(this);
    this.highlightAttributeSeries = this.highlightAttributeSeries.bind(this);
    this.clearSeriesHighlight = this.clearSeriesHighlight.bind(this);
    this.setActiveSlider = this.setActiveSlider.bind(this);
    this.clearActiveSlider = this.clearActiveSlider.bind(this);
    this.showSliderTooltip = this.showSliderTooltip.bind(this);
    this.hideSliderTooltip = this.hideSliderTooltip.bind(this);
    this.getAttributeColor = this.getAttributeColor.bind(this);
    this.getBothUsersAttributeData = this.getBothUsersAttributeData.bind(this);
  }

  componentDidMount () {
    const isPrint = this.props?.isPrint || false;
    if (this.state.preferences.epa_graph || isPrint) {
      this.drawEPA();
      const pref = {
        epa_graph: 1,
      }
      this.setState({ preferences: pref });
    }

    // Set initial time range based on backend metadata
    this.setInitialTimeRange();

    // Ensure pace values are updated with latest trends data
    const updatedPaceValues = this.defaultPaceValue();
    this.setState({ pace_values: updatedPaceValues });
  }

  // Set initial time range based on backend data or saved preference
  setInitialTimeRange() {
    const { meeting } = this.props;

    // First, try to restore saved time range from localStorage
    const storageKey = `meeting_pace_timeRange_${meeting.id}`;
    const savedTimeRange = localStorage.getItem(storageKey);

    if (savedTimeRange) {
      console.log('Restoring saved time range:', savedTimeRange);
      this.setState({ timeRange: savedTimeRange }, () => {
        // Fetch filtered data for the saved time range and redraw graph
        this.fetchFilteredEPAData(savedTimeRange);
      });
      return;
    }

    // Fallback to default time range if no saved preference
    const defaultTimeRange = 'Last 3 months';
    this.setState({ timeRange: defaultTimeRange }, () => {
      // Fetch filtered data for the default time range
      this.fetchFilteredEPAData(defaultTimeRange);
    });
  }

  componentDidUpdate (prevProps, prevState) {
    if (prevState.preferences.epa_graph !== this.state.preferences.epa_graph) {
      if (this.state.preferences.epa_graph) {
        this.drawEPA();
      }
    }

    // Update time range if meeting data changes
    if (prevProps.meeting !== this.props.meeting) {
      this.setInitialTimeRange();
    }

    // Update pace values if meeting trends data changes
    if (prevProps.meeting_trends !== this.props.meeting_trends) {
      const updatedPaceValues = this.defaultPaceValue();
      this.setState({ pace_values: updatedPaceValues });
    }
  }

  getEpaColor (id) {
    if (!id) return '#1f4260';
    return this.getAttributeColor(id);
  }

  updateCoachingText (id, value) {
    const { epa_coaching_text } = this.props;
    // check if id is custom_pace_ratings
    if (id.includes('custom_')) {
      const attribute = epa_coaching_text.custom_pace_attributes.find(
        attr => attr.id === Number(id.replace('custom_', ''))
      );
      const rating = attribute.ratings.find(r => r.rating_value === value);
      const updateText = {
        title: rating.rating_label,  // Maps to the header text
        text: rating.rating_desc,    // Maps to the description text
        color: this.getEpaColor(id)
      };
      this.setState({ coaching_text: updateText });
    } else {
      const updateText = epa_coaching_text[id].ratings[value - 1];
      updateText.color = this.getEpaColor(id);
      this.setState({ coaching_text: updateText });
    }
  }

  arrowClick (displayEpa) {
    const { meeting } = this.props;
    const isOpen = displayEpa ? 0 : 1;
    const data = { id: meeting.id, isOpen, type: 'epa_graph' };
    $.post(`${wbConfig.baseUrl}/meeting/saveMeetingPreferences`, data);
    const pref = {
      epa_graph: isOpen,
    }
    this.setState({ preferences: pref });
  }

  drawEPA () {
    const { meeting_trends } = this.props;
    const { meeting } = this.props;
    const meetingId = meeting.id || '';
    const containerId = `#meeting-pace-graph-${meetingId}`;
    const meetingSeries = (typeof meeting_trends.series !== 'undefined') ? meeting_trends.series : {};
    $(containerId).html('');
    if ((!Object.keys(meetingSeries).length)) {
      const img = `<img src="${wbConfig.baseUrl}/images/meetings/1on1emptygraph.png" width="350" />`;
      $(containerId).html(img);
    } else if (this.state.preferences.epa_graph) {
      this.renderEPAGraph(containerId, meeting_trends.x, meeting_trends.meetingOrder, meeting_trends.series, meeting_trends.owner);
    }
  }

  /**
   * buildSeriesConfig - build series configuration for highcharts
   * @param {*} data array of data points
   * @param {*} ownerId userId of the current user
   * @param {*} dataOwnerId userId of the series data
   * @param {*} fillColor string hex color
   * @returns object highcharts series configuration
   */
  buildSeriesConfig(data, ownerId, dataOwnerId, fillColor, attributeType) {
    const { highlightedAttribute } = this.state;
    const isOwner = (ownerId == dataOwnerId);
    const isHighlighted = highlightedAttribute && (highlightedAttribute === attributeType);
    const shouldDim = highlightedAttribute && (highlightedAttribute !== attributeType);

    // Calculate styling based on highlight state
    const opacity = shouldDim ? 0.3 : 1;
    const lineWidth = isHighlighted ? 2 : 1;
    const markerRadius = isHighlighted ? 5 : 4;

    return {
      connectNulls: true,
      data: reKeyArray(data),
      color: fillColor,
      opacity,
      isOwner: isOwner, // Add flag for identifying owner series
      marker: {
        fillColor: isOwner ? fillColor : '#ffffff',
        lineColor: null,
        lineWidth: isOwner ? '0' : 1,
        symbol: isOwner ? 'circle' : 'diamond',
        radius: markerRadius
      },
      lineWidth: lineWidth,
      dashStyle: isOwner ? 'Solid' : 'Dash',
      pointPlacement: isOwner ? 0 : -0.1,
      events: {
        click (event) {
          console.log('perform click', event);
        }
      }
    };
  }

  buildSeriesItem (arr, value, meetingId, attributeType, userId) {
    const dupOffset = 0.15;
    let offsetValue = value;
    // we allow nulls in the series so the category (X axis) shows
    if (value !== null) {
      // get duplicate count for this attribute value
      const count = arr.reduce((accumulator, arrayItem) => accumulator += (arrayItem == value ? 1 : 0), 0);
      if (count) {
        offsetValue = value + count * dupOffset;
      }
    }

    return {
      name: value,    // actual value to display in tooltip
      y: offsetValue,  // raw value to plot dot
      meetingId,
      attributeType,  // e.g., 'perform', 'custom_42'
      userId,         // user who made this rating
    }
  }



  renderEPAGraph (containerId, categories, meetingOrder, series, ownerId) {
    const { epa_coaching_text, t } = this.props;
    const seriesData = [];
    const categoriesLen = categories.length;
    const performEnabled = epa_coaching_text.perform?.enabled;
    const alignEnabled = epa_coaching_text.align?.enabled;
    const compEnabled = epa_coaching_text.comp?.enabled;
    const engageEnabled = epa_coaching_text.engage?.enabled;
    const attribute5Enabled = epa_coaching_text.attribute5?.enabled;

    // Custom attributes (array of enabled custom attributes)
    const customEnabledAttributes = (epa_coaching_text.custom_pace_attributes || []).filter(attr => attr.enabled);

    if (typeof series === 'object') {
      for (const k in series) {
        if (Object.hasOwn(series, k)) {
          // attributes broken out to control ordering of
          // dup checking.
          const eVal = [];
          const pVal = [];
          const aVal = [];
          const cVal = [];
          const a5Val = [];
          const mIds = [];

          // Custom attribute arrays
          const customVals = {};
          customEnabledAttributes.forEach(attr => {
            customVals[`custom_${String(attr.id)}`] = [];
          });

          for (const orderIndex in meetingOrder) {
            if (Object.hasOwn(meetingOrder, orderIndex)) {
              const meetingId = meetingOrder[orderIndex]

              eVal.push(nullifyUndefined(series[k].e[meetingId]));
              pVal.push(nullifyUndefined(series[k].p[meetingId]));
              aVal.push(nullifyUndefined(series[k].a[meetingId]));
              cVal.push(nullifyUndefined(series[k].c[meetingId]));
              a5Val.push(nullifyUndefined(series[k].a5[meetingId]));

              // Add custom attributes
              customEnabledAttributes.forEach(attr => {
                const key = `custom_${String(attr.id)}`;
                const value = nullifyUndefined(series[k][key] ? series[k][key][meetingId] : null);
                customVals[key].push(value);
              });

              mIds.push(meetingId);
            }
          }

          // attribute finalized series data
          const pS = [];
          const aS = [];
          const cS = [];
          const eS = [];
          const a5S = [];

          // Custom attribute series data
          const customSeriesData = {};
          customEnabledAttributes.forEach(attr => {
            const key = `custom_${String(attr.id)}`;
            customSeriesData[key] = [];
          });

          for (let i = 0; i < categoriesLen; i++) {
            const _xpos = [];
            const meetingId = mIds[i];

            // code finds dots that are duplicated and shifts them up/down
            // so they do not overlap.  Solution falls apart as more attributes
            // are added as they get closer to the next whole number on the graph.
            if (performEnabled) {
              pS[i] = this.buildSeriesItem(_xpos, pVal[i], meetingId, 'perform', k);
              _xpos.push(pVal[i]);
            }
            if (alignEnabled) {
              aS[i] = this.buildSeriesItem(_xpos, aVal[i], meetingId, 'align', k);
              _xpos.push(aVal[i]);
            }
            if (compEnabled) {
              cS[i] = this.buildSeriesItem(_xpos, cVal[i], meetingId, 'comp', k);
              _xpos.push(cVal[i]);
            }
            if (engageEnabled) {
              eS[i] = this.buildSeriesItem(_xpos, eVal[i], meetingId, 'engage', k);
              _xpos.push(eVal[i]);
            }
            if (attribute5Enabled) {
              a5S[i] = this.buildSeriesItem(_xpos, a5Val[i], meetingId, 'attribute5', k);
              _xpos.push(a5Val[i]);
            }

            // Process custom attributes
            customEnabledAttributes.forEach(attr => {
              const key = `custom_${String(attr.id)}`;
              customSeriesData[key][i] = this.buildSeriesItem(_xpos, customVals[key][i], meetingId, key, k);
              _xpos.push(customVals[key][i]);
            });
          }

          // Data arrays are re-keyed below because logic above assigns values to specific array keys, which
          // allows for possibility of skipping keys, which causes an error in the highcharts code
          const colors = this.defaultPaceColors();
          const standardColors = colors.standard || {};

          if (performEnabled) {
            seriesData.push(this.buildSeriesConfig(pS, ownerId, k, standardColors.perform || '#1f4260', 'perform'));
          }

          if (alignEnabled) {
            seriesData.push(this.buildSeriesConfig(aS, ownerId, k, standardColors.align || '#66cccc', 'align'));
          }

          if (compEnabled) {
            seriesData.push(this.buildSeriesConfig(cS, ownerId, k, standardColors.competence || '#FFA500', 'comp'));
          }

          if (engageEnabled) {
            seriesData.push(this.buildSeriesConfig(eS, ownerId, k, standardColors.engagement || '#78c047', 'engage'));
          }

          if (attribute5Enabled) {
            seriesData.push(this.buildSeriesConfig(a5S, ownerId, k, standardColors.attribute5 || '#FF69B4', 'attribute5'));
          }

          // Add custom attributes to series data
          customEnabledAttributes.forEach((attr, index) => {
            const key = `custom_${String(attr.id)}`;
            const color = colors.custom[index] || '#000000'; // fallback to black if no color available
            if (customSeriesData[key]) {
              seriesData.push(this.buildSeriesConfig(customSeriesData[key], ownerId, k, color, key));
            }
          });
        }
      }
    }

    var deviceW = (typeof deviceW === 'undefined') ? null : deviceW;
    var deviceH = (typeof deviceH === 'undefined') ? 450 : deviceH;

    const plotBands = [];
    for (let i = 0; i < categories.length; i += 2) {
      plotBands.push({
        from: i - 0.5,
        to: i + 0.5,
        color: 'rgba(200,200,200,0.2)' // light grey
      });
    }

    $(containerId).highcharts({
      chart: {
        style: {
          fontFamily: 'Helvetica, Arial, sans-serif',
          fontSize: '1rem',
        },
        height: deviceH,
        width: deviceW,
        type: 'line',
        spacingTop: 30,
        spacingRight: 30,
        spacingBottom: 30,
        spacingLeft: 30,
        plotBackgrosundColor: '#ffffff',
        plotBorderWidth: 0,
        plotBorderColor: '#ffffff',
      },
      // Pass props for tooltip access
      epa_coaching_text: this.props.epa_coaching_text,
      currentUser: this.props.currentUser,
      selectedUser: this.props.selectedUser,
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
      xAxis: {
        categories,
        plotBands,
        tickWidth: 0,
        tickLength: 0,
        lineColor: '#efefef',
        labels: {
          style: {
            fontSize: getFontSizeForCategories(categoriesLen),
          },
          formatter () {
            if (typeof this.value !== 'number') {
              return formatDateString(this.value);
            }
            return '';
          },
        },
        min: 0,
      },
      yAxis: {
        title: null,
        tickWidth: 0,
        tickLength: 0,
        tickInterval: 1,
        gridLineWidth: 1,
        gridLineColor: '#efefef',
        min: 0,
        max: 6,
        labels: {
          style: {
            fontSize: '10px',
            color: '#666',
          },
          formatter () {
            // we only allow PACE values up to 5 but dots
            // may shift above 5 due to duplication.  This
            // is to show the 6th y axis line but not its
            // label.
            if (this.value < 6) {
              return this.value;
            }
            return '';
          },
        },
      },
      scrollbar: {
        enabled: false,
      },
      plotOptions: {
        spline: {
          marker: {
            enabled: true,
            symbol: 'circle',
            radius: 3,
          },
        },
      },
      series: seriesData,
      legend: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        useHTML: true,
        formatter () {
          const point = this.point;
          const chart = this.series.chart;
          const { epa_coaching_text, currentUser, selectedUser } = chart.options;

          if (!point || point.name === null) return '';

          const attributeType = point.attributeType;
          const meetingId = point.meetingId;

          // Get date from categories array using point's x position
          const categories = chart.xAxis[0].categories;
          const dateString = categories[point.x];

          // Convert date string to formatted date (e.g., "9/29/2023" to "Sep 29, 2023")
          const formattedDate = formatDateString(dateString);

          // Find all data points for both users at this x position and attribute type
          const bothUsersData = [];
          chart.series.forEach(series => {
            if (series.visible && series.data) {
              const matchingPoint = series.data.find(p =>
                p && p.attributeType === attributeType &&
                p.x === point.x && p.meetingId === meetingId
              );
              if (matchingPoint && matchingPoint.name !== null) {
                const isOwner = series.userOptions.isOwner;
                const userId = matchingPoint.userId;
                const isCurrentUser = userId == currentUser.id;
                const userName = isCurrentUser ? currentUser.firstname : selectedUser.firstname;
                const userType = isOwner ? t('Owner') : t('Member');
                // Get rating information
                let ratingLabel = '';
                let ratingDesc = '';
                const value = matchingPoint.name;

                if (attributeType && attributeType.startsWith('custom_')) {
                  // Custom attribute
                  const customAttrId = attributeType.replace('custom_', '');
                  const customAttr = epa_coaching_text.custom_pace_attributes?.find(
                    attr => attr.id == customAttrId
                  );
                  if (customAttr) {
                    const rating = customAttr.ratings.find(r => r.rating_value == value);
                    if (rating) {
                      ratingLabel = rating.rating_label;
                      ratingDesc = rating.rating_desc;
                    }
                  }
                } else if (attributeType) {
                  // Standard attribute
                  const attrData = epa_coaching_text[attributeType];
                  if (attrData && attrData.ratings && attrData.ratings[value - 1]) {
                    const rating = attrData.ratings[value - 1];
                    ratingLabel = rating.title;
                    ratingDesc = rating.text;
                  }
                }

                bothUsersData.push({
                  userName,
                  userType,
                  value,
                  ratingLabel,
                  ratingDesc,
                  isOwner
                });
              }
            }
          });

          // Sort so owner appears first
          bothUsersData.sort((a, b) => b.isOwner - a.isOwner);

          if (bothUsersData.length === 0) return '';

          // Get attribute name for display
          let attributeName = '';
          if (attributeType && attributeType.startsWith('custom_')) {
            // Custom attribute
            const customAttrId = attributeType.replace('custom_', '');
            const customAttr = epa_coaching_text.custom_pace_attributes?.find(
              attr => attr.id == customAttrId
            );
            if (customAttr) {
              attributeName = customAttr.name;
            }
          } else if (attributeType) {
            // Standard attribute
            const attrData = epa_coaching_text[attributeType];
            if (attrData && attrData.name) {
              attributeName = attrData.name;
            }
          }

          // Build tooltip content showing both users
          let tooltipContent = `
            <div style="
              padding: 8px;
              line-height: 1.4;
              word-wrap: break-word;
              overflow-wrap: break-word;
              hyphens: auto;
              background: white;
              border-radius: 6px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              position: relative;
              z-index: 9999999;
              min-width: 250px;
            ">
              ${attributeName ? `<div style="font-weight: bold; margin-bottom: 8px; color: #444; border-bottom: 1px solid #eee; padding-bottom: 4px;">${attributeName}</div>` : ''}
          `;

          bothUsersData.forEach((userData, index) => {
            if (index > 0) {
              tooltipContent += '<div style="margin: 4px 0; padding-top: 4px;"></div>';
            }
            tooltipContent += `
              <div style="margin-bottom: 4px;">
                <div style="font-weight: bold; margin-bottom: 4px; word-wrap: break-word;">${userData.userName} (${userData.userType})</div>
                <div style="margin-bottom: 4px; word-wrap: break-word;">${userData.value} · ${userData.ratingLabel}</div>
                <div style="width: 200px; color: #666; font-size: 12px; word-wrap: break-word; overflow-wrap: break-word; white-space: normal;">${userData.ratingDesc}</div>
              </div>
            `;
          });

          tooltipContent += `
            <div style="margin-top: 4px; padding-top: 4px; color: #888; font-size: 11px; word-wrap: break-word;">${formattedDate}</div>
          </div>`;

          return tooltipContent;
        },
      },
    });
  }

  updateGraph () {
    const _this = this;
    const { meeting, actions } = this.props;
    const { pace_values } = this.state;
    if (typeof meeting.id === 'undefined') return;
    const params = {
      meeting_id: meeting.id,
      engage: pace_values.engage,
      align: pace_values.align,
      perform: pace_values.perform,
      comp: pace_values.comp,
      attribute5: pace_values.attribute5,
    };

    // Add custom pace ratings if they exist
    if (pace_values.custom_pace_ratings && pace_values.custom_pace_ratings.length > 0) {
      const customPaceRatings = pace_values.custom_pace_ratings.map(rating => ({
        attributeId: rating.attributeId,
        rating_value: rating.rating
      }));
      params.custom_pace_ratings = JSON.stringify(customPaceRatings);
    }

    showLoaderWidget($('#content'));
    $.ajax({
      url: `${wbConfig.baseUrl}/meeting/updateMeetingEPA`,
      type: 'post',
      data: params,
    })
      .done((response) => {
        removeLoader();
        if (response.error) {
          console.log(response.error);
        } else {
          actions.MeetingUpdatePaceValues(response.data);
          _this.props.setConversationMeetingPaceRatings(response.data);
          // Respect the selected time range
          _this.fetchFilteredEPAData(_this.state.timeRange);
        }
      })
      .fail((response) => {
        removeLoader();
        console.log('Please try again later');
      });
  }

  handleTimeRangeChange (event) {
    const selectedRange = event.target.value;
    this.setState({ timeRange: selectedRange });

    // Save time range to localStorage for persistence
    const { meeting } = this.props;
    const storageKey = `meeting_pace_timeRange_${meeting.id}`;
    localStorage.setItem(storageKey, selectedRange);

    // Convert time range to dates and fetch filtered data
    this.fetchFilteredEPAData(selectedRange);
  }

  defaultPaceColors () {
    const { defaultPaceColors } = this.props;
    return defaultPaceColors;
  }

  coachingText () {
    const { meeting_pace } = this.props;
    const { epa_coaching_text } = this.props;
    const performEnabled = epa_coaching_text.perform?.enabled;
    const alignEnabled = epa_coaching_text.align?.enabled;
    const compEnabled = epa_coaching_text.comp?.enabled;
    const engageEnabled = epa_coaching_text.engage?.enabled;
    const attribute5Enabled = epa_coaching_text.attribute5?.enabled;

    let coachingObj = {};
    coachingObj.color = this.getEpaColor();
    coachingObj.text = this.props.before_epa_text;
    if (Object.keys(meeting_pace).length) {
      if (performEnabled && meeting_pace.perform > 1) {
        coachingObj = epa_coaching_text.perform.ratings[meeting_pace.perform - 1];
        coachingObj.color = this.getEpaColor('perform');
      } else if (alignEnabled && meeting_pace.align > 1) {
        coachingObj = epa_coaching_text.align.ratings[meeting_pace.align - 1];
        coachingObj.color = this.getEpaColor('align');
      } else if (compEnabled && meeting_pace.comp > 1) {
        coachingObj = epa_coaching_text.comp.ratings[meeting_pace.comp - 1];
        coachingObj.color = this.getEpaColor('comp');
      } else if (engageEnabled && meeting_pace.engage > 1) {
        coachingObj = epa_coaching_text.engage.ratings[meeting_pace.engage - 1];
        coachingObj.color = this.getEpaColor('engage');
      } else if (attribute5Enabled && meeting_pace.attribute5 > 1) {
        coachingObj = epa_coaching_text.engage.ratings[meeting_pace.attribute5 - 1];
        coachingObj.color = this.getEpaColor('attribute5');
      }
    }
    return coachingObj;
  }

  // Fetch the latest meeting trends data (without date filtering)
  fetchLatestMeetingTrends() {
    const { meeting, actions } = this.props;

    // Prevent unnecessary API calls
    if (!meeting || !meeting.id) {
      console.log('Cannot fetch EPA data: missing meeting ID');
      return;
    }

    $.ajax({
      url: `${wbConfig.baseUrl}/restapi/meetings/meetingTrends`,
      type: 'GET',
      data: {
        id: meeting.id
      }
    })
      .done((response) => {
        if (response.error) {
          console.error('Error fetching latest EPA data:', response.error);
        } else {
          // Update the meeting trends data
          if (response.data) {
            // Update Redux state with latest data
            actions.MeetingUpdateTrends(response.data);

            // Re-draw the chart with updated data
            setTimeout(() => this.drawEPA(), 0);
          } else {
            console.warn('No meeting trends data received from server');
          }
        }
      })
      .fail((xhr, status, error) => {
        console.error('Failed to fetch latest EPA data:', error);
      });
  }

  // Fetch filtered EPA data based on time range
  fetchFilteredEPAData(timeRange) {
    const { meeting, actions } = this.props;

    // Prevent unnecessary API calls
    if (!meeting || !meeting.id) {
      console.log('Cannot fetch EPA data: missing meeting ID');
      return;
    }

    // Get meeting date for proper time range calculation
    const meetingDate = meeting.sched_at || null;
    const dateRange = MeetingPaceSection.getDateRangeFromSelection(timeRange, meetingDate);

    showLoaderWidget($('#content'));

    $.ajax({
      url: `${wbConfig.baseUrl}/restapi/meetings/meetingTrends`,
      type: 'GET',
      data: {
        id: meeting.id,
        startTime: dateRange.startTime,
        endTime: dateRange.endTime
      }
    }).done((response) => {
      removeLoader();
      if (response.error) {
        console.error('Error fetching filtered EPA data:', response.error);
        // Optionally show user-friendly error message
        // this.setState({ errorMessage: 'Failed to load chart data for selected time range' });
        return;
      }

      if (!response.data) {
        console.warn('No meeting trends data received from server');
        return;
      }

      const { startTime, endTime } = dateRange;
      const backendX = response.data.x || [];
      const backendMeetingOrder = response.data.meetingOrder || [];

      const { categories, meetingOrder } = MeetingPaceSection.extendXAxisWithTimeRange(
        backendX,
        backendMeetingOrder,
        startTime,
        endTime
      );

      const data = { ...response.data, x: categories, meetingOrder };
      actions.MeetingUpdateTrends(data);

      setTimeout(() => this.drawEPA(), 0);
    }).fail((xhr, status, error) => {
      removeLoader();
      console.error('Failed to fetch filtered EPA data:', error);
      // Optionally show user-friendly error message
      // this.setState({ errorMessage: 'Failed to load chart data. Please try again.' });
    });
  }

  highlightAttributeSeries (attributeType) {
    this.setState({ highlightedAttribute: attributeType }, () => this.drawEPA());
  }

  clearSeriesHighlight () {
    this.setState({ highlightedAttribute: null }, () => this.drawEPA());
  }

  setActiveSlider (attributeType) {
    this.setState({ activeSlider: attributeType });
  }

  clearActiveSlider () {
    this.setState({ activeSlider: null });
  }

  showSliderTooltip (attributeType, sliderId) {
    const data = this.getBothUsersAttributeData(attributeType);
    if (!data) {
      return;
    }

    // Get chart instance
    const chart = $(`#meeting-pace-graph-${this.props.meeting.id || ''}`).highcharts();

    // Check if there are any visible points for this attribute type
    if (chart?.series) {
      const hasVisiblePoints = chart.series.some(
        series => series.visible && series.points?.some(
          point => point?.attributeType === attributeType && point.isInside
        )
      );

      if (!hasVisiblePoints) {
        return;
      }
    }

    // Get the color for this attribute
    const attributeColor = this.getAttributeColor(attributeType);
    data.attributeColor = attributeColor;

    // Calculate tooltip position based on chart plot points
    const { meeting } = this.props;
    const containerId = `#meeting-pace-graph-${meeting.id ? meeting.id : ''}`;
    const chartContainer = $(containerId);

    if (chartContainer.length > 0) {
      try {
        const chart = chartContainer.highcharts();
        if (chart && chart.series) {
          // Find all series for the current attribute type (both users)
          const targetSeries = chart.series.filter(
            series => series.userOptions && series.userOptions.data &&
            series.userOptions.data.some(point => point && point.attributeType === attributeType)
          );

          if (targetSeries.length > 0) {
            // Get the most recent data points for this attribute from all series
            const targetPoints = [];
            targetSeries.forEach(series => {
              if (series.data && series.data.length > 0) {
                // Find the last point in this series that matches our attribute
                for (let i = series.data.length - 1; i >= 0; i--) {
                  const point = series.data[i];
                  if (point && point.attributeType === attributeType &&
                      point.plotX !== undefined && point.plotY !== undefined
                  ) {
                    targetPoints.push(point);
                    break;
                  }
                }
              }
            });

            if (targetPoints.length > 0) {
              // Calculate average position of the target points
              const avgX = targetPoints.reduce((sum, point) => sum + point.plotX, 0) / targetPoints.length;
              const avgY = targetPoints.reduce((sum, point) => sum + point.plotY, 0) / targetPoints.length;

              // Convert chart coordinates to screen coordinates
              const chartRect = chart.container.getBoundingClientRect();
              const position = {
                x: chartRect.left + chart.plotLeft + avgX, // Center horizontally
                y: chartRect.top + chart.plotTop + avgY - 80 // Position above the points
              };

              this.setState({
                sliderTooltip: {
                  visible: true,
                  attributeType: attributeType,
                  position: position,
                  data: data
                }
              });
              return;
            }
          }
        }
      } catch (error) {
        console.log('Error positioning tooltip at chart point:', error);
      }
    }

    // Fallback: position near the slider if chart positioning fails
    const sliderElement = document.getElementById(`oneonone-epa-${sliderId}`);
    if (sliderElement) {
      const sliderRect = sliderElement.getBoundingClientRect();
      const position = {
        x: sliderRect.left + sliderRect.width + 20,
        y: sliderRect.top + (sliderRect.height / 2) - 40
      };

      this.setState({
        sliderTooltip: {
          visible: true,
          attributeType: attributeType,
          position: position,
          data: data
        }
      });
    }
  }

  hideSliderTooltip () {
    this.setState({
      sliderTooltip: {
        visible: false,
        attributeType: null,
        position: { x: 0, y: 0 },
        data: null
      }
    });
  }

  getAttributeColor (attributeType) {
    const colors = this.defaultPaceColors();

    if (attributeType && attributeType.startsWith('custom_')) {
      // Custom attribute
      const customAttrId = attributeType.replace('custom_', '');
      const customAttrs = (
        this.props.epa_coaching_text.custom_pace_attributes || []).filter(attr => attr.enabled
      );
      const attrIndex = customAttrs.findIndex(attr => attr.id == customAttrId);
      return colors.custom[attrIndex] || '#000000';
    }
    // Standard attribute colors
    const standardColors = colors.standard || {};
    // Map short attribute names to their full names in the colors object
    const attributeMap = {
      'comp': 'competence',
      'engage': 'engagement'
    };
    const mappedAttribute = attributeMap[attributeType] || attributeType;
    return standardColors[mappedAttribute] || '#1f4260';
  }

  getBothUsersAttributeData (attributeType) {
    const { meeting_trends, meeting, currentUser, selectedUser, epa_coaching_text } = this.props;

    if (!meeting_trends || !meeting_trends.series || !meeting.id) {
      return null;
    }

    const meetingId = String(meeting.id);
    const currentUserId = String(currentUser.id);
    const selectedUserId = String(selectedUser.id);
    const meetingOwnerId = String(meeting.owner);

    // Get current values for both users
    const getUserData = (userId) => {
      const userSeries = meeting_trends.series[userId];
      if (!userSeries) return null;

      let value = null;
      let ratingLabel = '';
      let ratingDesc = '';
      let attributeName = '';

      if (attributeType && attributeType.startsWith('custom_')) {
        // Custom attribute
        const customAttrId = attributeType.replace('custom_', '');
        const customAttr = epa_coaching_text.custom_pace_attributes?.find(attr => attr.id == customAttrId);
        if (customAttr) {
          attributeName = customAttr.name;
          value = userSeries[attributeType] ? userSeries[attributeType][meetingId] : null;
          if (value !== null) {
            const rating = customAttr.ratings.find(r => r.rating_value == value);
            if (rating) {
              ratingLabel = rating.rating_label;
              ratingDesc = rating.rating_desc;
            }
          }
        }
      } else if (attributeType) {
        // Standard attribute
        const attrData = epa_coaching_text[attributeType];
        if (attrData && attrData.ratings) {
          attributeName = attrData.name;
          // Map attribute types to series data keys
          const attrKeyMap = {
            'perform': 'p',
            'align': 'a',
            'comp': 'c',
            'engage': 'e',
            'attribute5': 'a5'
          };
          const seriesKey = attrKeyMap[attributeType];
          if (seriesKey && userSeries[seriesKey]) {
            value = userSeries[seriesKey][meetingId];
            if (value !== null && value !== undefined && attrData.ratings[value - 1]) {
              const rating = attrData.ratings[value - 1];
              ratingLabel = rating.title;
              ratingDesc = rating.text;
            }
          }
        }
      }

      return {
        value,
        ratingLabel,
        ratingDesc,
        attributeName
      };
    };

    const currentUserData = getUserData(currentUserId);
    const selectedUserData = getUserData(selectedUserId);

    // Determine which user is the owner and which is the member
    const isCurrentUserOwner = currentUserId === meetingOwnerId;
    const ownerData = isCurrentUserOwner ? currentUserData : selectedUserData;
    const memberData = isCurrentUserOwner ? selectedUserData : currentUserData;

    return {
      owner: {
        ...ownerData,
        name: isCurrentUserOwner ? currentUser.firstname : selectedUser.firstname,
        isCurrentUser: isCurrentUserOwner
      },
      member: {
        ...memberData,
        name: isCurrentUserOwner ? selectedUser.firstname : currentUser.firstname,
        isCurrentUser: !isCurrentUserOwner
      },
      attributeName: currentUserData?.attributeName || selectedUserData?.attributeName || ''
    };
  }

  defaultPaceValue () {
    const {
      meeting_pace = {},
      epa_coaching_text,
      meeting_trends,
      meeting,
      currentUser
    } = this.props;
    const customAttrs = epa_coaching_text.custom_pace_attributes || [];

    // 1. Copy all existing top-level keys (shallow copy) and set defaults for standard attributes
    const paceValues = {
      perform: meeting_pace.perform || 1,
      align: meeting_pace.align || 1,
      comp: meeting_pace.comp || 1,
      engage: meeting_pace.engage || 1,
      attribute5: meeting_pace.attribute5 || 1,
      ...meeting_pace
    };

    // 2. Build a map of existing custom ratings (if any)
    const ratingsMap = {};
    if (Array.isArray(meeting_pace.custom_pace_ratings)) {
      meeting_pace.custom_pace_ratings.forEach(r => {
        ratingsMap[r.attributeId] = r.rating;
      });
    }

    // 3. Get the latest custom attribute values from meeting trends data
    if (meeting_trends && meeting_trends.series && meeting && meeting.id) {
      const meetingId = String(meeting.id);
      const userId = String(currentUser.id);
      const userSeries = meeting_trends.series[userId];

      if (userSeries) {
        customAttrs.forEach(attr => {
          const customKey = `custom_${String(attr.id)}`;
          if (userSeries[customKey] && userSeries[customKey][meetingId] !== undefined) {
            const latestValue = userSeries[customKey][meetingId];
            if (latestValue !== null) {
              ratingsMap[attr.id] = String(latestValue);
            }
          }
        });
      }
    }

    // 4. Build the custom_pace_ratings array, ensuring all enabled attributes are present
    paceValues.custom_pace_ratings = customAttrs
      .filter(attr => attr.enabled)
      .map(attr => {
        // Use existing rating if present, otherwise default to first rating_id
        let rating = ratingsMap[attr.id];
        if (rating === undefined && Array.isArray(attr.ratings) && attr.ratings.length > 0) {
          rating = '1';
        }
        return {
          attributeId: attr.id,
          rating
        };
      });

    return paceValues;
  }

  render () {
    const {
      coaching_text, timeRange, preferences, pace_values, activeSlider,
    } = this.state;
    const epaHeaderTitle = coaching_text.title || '';
    const epaText = coaching_text.text || '';
    const epaColor = coaching_text.color || '';
    const { selectedUser, isPrint, currentUser, meeting, epa_coaching_text, t } = this.props;
    const meetingOwner = meeting.owner;
    const meetingOpen = (meeting.status == 1);
    const keepOpen = $('.meetingLeftMenu').attr('keepopen');
    const personName = selectedUser.fullName || '';
    const performEnabled = epa_coaching_text.perform?.enabled;
    const alignEnabled = epa_coaching_text.align?.enabled;
    const compEnabled = epa_coaching_text.comp?.enabled;
    const engageEnabled = epa_coaching_text.engage?.enabled;
    const attribute5Enabled = epa_coaching_text.attribute5?.enabled;
    let legend1 = meetingOwner == currentUser.id ? t('You') : selectedUser.firstname;
    let legend2 = meeting.user_id == currentUser.id ? t('You') : selectedUser.firstname;

    // conversation props
    const { conversationMeeting, isConversationMeeting, isConversationMeetingCompleted } = this.props;
    if (isConversationMeeting) {
      legend1 = conversationMeeting.managerId == currentUser.id ? t('You') : selectedUser.firstname;
      legend2 = conversationMeeting.memberId == currentUser.id ? t('You') : selectedUser.firstname;
    }
    const disabledUpdateGraph = isConversationMeetingCompleted ?? false;

    const innerStyled = isPrint ? {
      'box-shadow': 'none',
      '-moz-box-shadow': 'none',
      '-webkit-box-shadow': 'none',
      'pointer-events': 'none'
    } : {};

    const panelStyled = isPrint ? {
      display: 'flex',
      justifyContent: 'space-between',
      cursor: 'pointer',
      backgroundColor: '#fff',
      padding: '15px 0px 15px 15px',
      borderRadius: '4px',
      width: '90%',
    } : {};

    const labelStyled = isPrint ? {
      fontFamily: 'Lato',
      fontSize: '14px',
      fontWeight: 'bold',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.43',
      letterSpacing: '0.15px',
      color: 'rgba(0, 0, 0, 0.6)'
    } : {};

    // Get enabled custom attributes
    const customEnabledAttributes = (epa_coaching_text.custom_pace_attributes || []).filter(attr => attr.enabled);

    // Get API colors
    const apiColors = this.defaultPaceColors();
    const standardColors = apiColors.standard || {};

    // Helper function to get slider container style
    const getSliderContainerStyle = (attributeType) => {
      if (activeSlider && activeSlider !== attributeType) {
        return { opacity: 0.3, transition: 'opacity 0.3s ease' };
      }
      return { opacity: 1, transition: 'opacity 0.3s ease' };
    };

    return (
      <div className="oneononebox-inner" style={{ ...innerStyled }}>
        <div>
          <div
            className="oneonone-label width100"
            style={{ cursor: 'pointer', ...panelStyled }}
            onClick={() => this.arrowClick(preferences.epa_graph)}
          >
            <div className="left width80">
              <div>
                <div className="left width100" style={{ width: '95%' }}>
                  <span
                    className="vg-1on1"
                    style={{ ...labelStyled }}
                    dangerouslySetInnerHTML={{ __html: epa_coaching_text.header.name }}
                  />{' '}
                  {!epa_coaching_text?.isMeetingsCustomPaceEnabled && (
                    <span className="wb-help-icon">
                      <img
                        className="openGoalsHelpText"
                        data-attr="meeting_epa_trend_help"
                        data-source-attr="meetings"
                        src={`${wbConfig.baseUrl}/images/goal/help_small.png`}
                      />
                    </span>
                  )}
                </div>
              </div>
            </div>
            {!isPrint && (
              <div className="right width20">
                <div className={`wb-down-arrow right ${preferences.epa_graph ? 'wb-up-arrow' : 'wb-down-arrow'}`} />
                <div className="clear" />
              </div>
            )}
            <div className="clear" />
          </div>
          <div className="oneonone-expand " style={{ display: preferences.epa_graph ? 'block' : 'none' }}>
            <div className="clear" />
            <div className="width100">
              <div
                className="width100"
                style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '20px' }}
              >
                <div className="meeting-legend-wrapper">
                  <div className="meeting-legend" style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '5px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span className="meeting-legend-manager" />
                      <span className="meeting-legend-label">{legend1}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span className="meeting-legend-member" />
                      <span className="meeting-legend-label">{legend2}</span>
                    </div>
                  </div>
                </div>
                <div className="time-range-selector">
                  <FormControl size="small" style={{ minWidth: '110px' }} variant="outlined">
                    <Select
                      value={timeRange}
                      onChange={this.handleTimeRangeChange}
                      displayEmpty
                      style={{
                        fontSize: '12px',
                        border: '1px solid #ccc',
                        height: '32px'
                      }}
                    >
                      <MenuItem value="Last month">{t('Last month')}</MenuItem>
                      <MenuItem value="Last 3 months">{t('Last 3 months')}</MenuItem>
                      <MenuItem value="Last 6 months">{t('Last 6 months')}</MenuItem>
                      <MenuItem value="Last year">{t('Last year')}</MenuItem>
                      <MenuItem value="Last 2 years">{t('Last 2 years')}</MenuItem>
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className="oneonone-graph-wrapper">
                <div className="oneonone-graph left" style={{ width: keepOpen == 'true' ? '53%' : '60%' }}>
                  <div id={`meeting-pace-graph-${meeting.id ? meeting.id : ''}`} />
                </div>
                <div
                  className="left oneonone-epa-meter-wrapper"
                  style={{ marginLeft: '30px', width: keepOpen == 'true' ? '30%' : '35%' }}
                >
                  <div className="oneonone-epa-level-wrapper left">
                    <div className="oneonone-epa-levels">
                      {performEnabled && (
                        <div style={getSliderContainerStyle('perform')}>
                          <div
                            className="epa-level-text"
                            dangerouslySetInnerHTML={{ __html: epa_coaching_text.perform.name }}
                          />
                          <PACESlider
                            id="performance"
                            class_suffix="pref"
                            parent={this}
                            disabled={!meetingOpen}
                            stateId="perform"
                            value={pace_values.perform}
                            color={standardColors.perform || '#1f4260'}
                            onSliderStart={() => {
                              this.setActiveSlider('perform');
                            }}
                            onSliderEnd={() => {
                              this.clearActiveSlider();
                              this.hideSliderTooltip();
                            }}
                          />
                        </div>
                      )}
                      {alignEnabled && (
                        <div style={getSliderContainerStyle('align')}>
                          <div
                            className="epa-level-text"
                            dangerouslySetInnerHTML={{ __html: epa_coaching_text.align.name }}
                          />
                          <PACESlider
                            id="alignment"
                            class_suffix="alignment"
                            parent={this}
                            disabled={!meetingOpen}
                            stateId="align"
                            value={pace_values.align}
                            color={standardColors.align || '#66cccc'}
                            onSliderStart={() => {
                              this.setActiveSlider('align');
                            }}
                            onSliderEnd={() => {
                              this.clearActiveSlider();
                              this.hideSliderTooltip();
                            }}
                          />
                        </div>
                      )}
                      {compEnabled && (
                        <div style={getSliderContainerStyle('comp')}>
                          <div
                            className="epa-level-text"
                            dangerouslySetInnerHTML={{ __html: epa_coaching_text.comp.name }}
                          />
                          <PACESlider
                            id="competence"
                            class_suffix="competence"
                            parent={this}
                            disabled={!meetingOpen}
                            stateId="comp"
                            value={pace_values.comp}
                            color={standardColors.competence || '#FFA500'}
                            onSliderStart={() => {
                              this.setActiveSlider('comp');
                            }}
                            onSliderEnd={() => {
                              this.clearActiveSlider();
                              this.hideSliderTooltip();
                            }}
                          />
                        </div>
                      )}
                      {engageEnabled && (
                        <div style={getSliderContainerStyle('engage')}>
                          <div
                            className="epa-level-text"
                            dangerouslySetInnerHTML={{ __html: epa_coaching_text.engage.name }}
                          />
                          <PACESlider
                            id="engagement"
                            class_suffix="ega"
                            parent={this}
                            disabled={!meetingOpen}
                            stateId="engage"
                            value={pace_values.engage}
                            color={standardColors.engagement || '#78c047'}
                            onSliderStart={() => {
                              this.setActiveSlider('engage');
                            }}
                            onSliderEnd={() => {
                              this.clearActiveSlider();
                              this.hideSliderTooltip();
                            }}
                          />
                        </div>
                      )}
                      {attribute5Enabled && (
                        <div style={getSliderContainerStyle('attribute5')}>
                          <div
                            className="epa-level-text"
                            dangerouslySetInnerHTML={{ __html: epa_coaching_text.attribute5.name }}
                          />
                          <PACESlider
                            id="attribute5"
                            class_suffix="ega"
                            parent={this}
                            disabled={!meetingOpen}
                            stateId="attribute5"
                            value={pace_values.attribute5}
                            color={standardColors.attribute5 || '#FF69B4'}
                            onSliderStart={() => {
                              this.setActiveSlider('attribute5');
                            }}
                            onSliderEnd={() => {
                              this.clearActiveSlider();
                              this.hideSliderTooltip();
                            }}
                          />
                        </div>
                      )}
                      {customEnabledAttributes.map((attr, idx) => (
                        <div key={attr.id} style={getSliderContainerStyle(`custom_${String(attr.id)}`)}>
                          <div className="epa-level-text" dangerouslySetInnerHTML={{ __html: attr.name }} />
                          <PACESlider
                            id={`custom_${attr.id}`}
                            class_suffix="custom"
                            parent={this}
                            disabled={!meetingOpen}
                            stateId={`custom_${attr.id}`}
                            value={pace_values.custom_pace_ratings.find((r) => r.attributeId === attr.id)?.rating || 1}
                            color={apiColors.custom[idx] || '#000000'}
                            onSliderStart={() => {
                              this.setActiveSlider(`custom_${String(attr.id)}`);
                            }}
                            onSliderEnd={() => {
                              this.clearActiveSlider();
                              this.hideSliderTooltip();
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="clear" />
                  </div>
                  <div className="oneonone-epa-level-title-wrapper left">
                    {meetingOpen && !isPrint ? (
                      <div>
                        <div
                          className="oneonone-epa-level-title-text"
                          style={{ color: epaColor }}
                          dangerouslySetInnerHTML={{ __html: epaHeaderTitle }}
                        />
                        <div
                          className="oneonone-epa-level-title-description"
                          dangerouslySetInnerHTML={{ __html: epaText }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '3px',
                            left: '0',
                            right: '0',
                            width: '120px',
                          }}
                        >
                          {meeting.sched_at > 0 ? (
                            <div className="oneonone-epa-level update-btn-wrapper">
                              {disabledUpdateGraph ? (
                                <div className="update-btn-link clickable">{t('Update graph')}</div>
                              ) : (
                                <div
                                  role="button"
                                  className="update-btn-link clickable"
                                  onClick={() => this.updateGraph()}
                                  onMouseDown={() => this.updateGraph()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') this.updateGraph();
                                  }}
                                  tabIndex="0"
                                >
                                  {t('Update graph')}
                                </div>
                              )}
                            </div>
                          ) : (
                            ''
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="oneonone-epa-level-title-description">
                        {t("Here's where you left things off with {{personName}}.", { personName })}
                      </div>
                    )}
                  </div>
                  <div className="clear" />
                </div>
                <div className="clear" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
export default withTranslation()(MeetingPaceSection);
